<template>
	<div
		data-testid="help-assistant-chat-list"
		class="help-chat-list"
	>
		<help-button
			ref="newChatButton"
			data-testid="help-assistant-chat-list-new-chat"
			variant="outline"
			:disabled="disabled"
			class="help-chat-list__new-chat"
			@click="$emit('new-chat')"
		>
			New chat
		</help-button>

		<!--
			Mirrors the React ChatList's loading Marker: a spinner beside shimmering copy, so the wait
			visibly moves rather than sitting there as static text. The spinner is decoration; the
			copy carries the meaning. .shimmer itself lives in shared/styles/shared-styles.css.
		-->
		<p
			v-if="loading"
			data-testid="help-assistant-chat-list-loading"
			class="help-chat-list__hint help-chat-list__hint--loading"
		>
			<LucideIcon
				name="Loader2"
				:size="14"
				aria-hidden="true"
				class="help-chat-list__spinner"
			/>
			<span class="shimmer">
				Loading chats…
			</span>
		</p>
		<p
			v-else-if="chats.length === 0"
			data-testid="help-assistant-chat-list-empty"
			class="help-chat-list__hint"
		>
			No previous chats yet.
		</p>
		<ul
			v-else
			data-testid="help-assistant-chat-list-items"
			class="help-chat-list__items"
		>
			<li
				v-for="chat in chats"
				:key="chat.chatId"
				class="help-chat-list__row"
			>
				<button
					type="button"
					:data-testid="`help-assistant-chat-list-item-${chat.chatId}`"
					:aria-current="chat.chatId === activeChatId ? 'true' : null"
					:disabled="disabled"
					:class="['help-chat-list__select', { 'help-chat-list__select--active': chat.chatId === activeChatId }]"
					@click="$emit('select', { chatId: chat.chatId })"
				>
					<span class="help-chat-list__select-heading">
						<span class="help-chat-list__select-title">
							{{ chat.title }}
						</span>
						<span
							v-if="chat.chatId === activeChatId"
							data-testid="help-assistant-chat-list-active-badge"
							class="help-chat-list__select-badge"
						>
							Current
						</span>
					</span>
					<span class="help-chat-list__select-time">
						{{ formatRelativeTime(chat.updatedAt) }}
					</span>
				</button>

				<button
					:ref="`delete-${chat.chatId}`"
					type="button"
					:data-testid="`help-assistant-chat-list-delete-${chat.chatId}`"
					:aria-label="`Delete chat \u0022${chat.title}\u0022`"
					:disabled="disabled"
					class="help-chat-list__icon-button help-chat-list__icon-button--emphasis"
					@click="startDelete(chat.chatId)"
				>
					<!-- An X, as the React ChatList draws its delete; the bin it replaced was the one glyph
					the two lists did not share. -->
					<LucideIcon
						name="X"
						:size="16"
					/>
				</button>
			</li>
		</ul>

		<!--
			The house confirm-delete modal, as the React ChatList confirms through its shared
			DeleteConfirmDialog; this list used to swap the row to an inline tick and cross, which was
			the one place the two disagreed on a pattern rather than a detail. Declared inside the sheet
			on purpose: Vuetify stacks a dialog two above the z-index it finds walking up from where it
			is declared, so this one lands above the sheet's overlay rather than behind it.
		-->
		<confirm-delete-modal
			v-if="pendingDelete"
			:entity-name="pendingDelete.title"
			entity-type="chat"
			@close="cancelDelete"
			@delete="confirmDelete"
		/>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import ConfirmDeleteModal from '../../../../../shared/components/confirm-delete-modal.vue'
import HelpButton from './help-button.vue'
import { formatRelativeTime } from './utils/chat-core.js'

export default {
	name: 'help-chat-list',
	components: {
		ConfirmDeleteModal,
		HelpButton,
		LucideIcon
	},
	props: {
		chats: {
			type: Array,
			default: () => []
		},
		activeChatId: {
			type: String,
			default: null
		},
		loading: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			// Chat the confirm-delete modal is open for. Reset whenever the chat list itself
			// changes underneath it (e.g. the pending chat was deleted from elsewhere) so a stale
			// modal can't linger.
			pendingDeleteId: null
		}
	},
	computed: {
		pendingDelete () {
			return this.chats.find(chat => chat.chatId === this.pendingDeleteId) ?? null
		}
	},
	watch: {
		chats () {
			if (this.pendingDeleteId && !this.chats.some(chat => chat.chatId === this.pendingDeleteId)) {
				this.pendingDeleteId = null
			}
		},
		// The house modal is persistent (Escape only shakes it), and the sheet stands down for keys
		// pressed inside a dialog, so Escape would otherwise do nothing at all. Listen while the
		// modal is up and treat it as Close.
		pendingDeleteId (chatId) {
			if (chatId) document.addEventListener('keydown', this.handleModalKeydown)
			else document.removeEventListener('keydown', this.handleModalKeydown)
		}
	},
	beforeDestroy () {
		document.removeEventListener('keydown', this.handleModalKeydown)
	},
	methods: {
		handleModalKeydown (event) {
			if (event.key === 'Escape') this.cancelDelete()
		},
		formatRelativeTime,
		startDelete (chatId) {
			this.pendingDeleteId = chatId
		},
		// The modal is mounted and unmounted with `v-if`, so Vuetify's own focus return never runs:
		// closing it would drop focus to <body> and keyboard/screen-reader users would lose their
		// place. Put it back on the row's X, via $nextTick so the modal has gone first.
		cancelDelete () {
			const chatId = this.pendingDeleteId
			this.pendingDeleteId = null
			this.$nextTick(() => this.focusRef(`delete-${chatId}`))
		},
		// Focus goes to New chat the moment the modal closes, not once the row has gone: the parent's
		// DELETE is async and may fail (it swallows the error and keeps the row), and either way the
		// unmounting modal would otherwise leave focus on <body>. New chat is there in both outcomes.
		confirmDelete () {
			const chatId = this.pendingDeleteId
			this.pendingDeleteId = null
			this.$emit('delete', { chatId })
			this.$nextTick(() => this.$refs.newChatButton?.$el?.focus())
		},
		// `ref` inside v-for is per-item unique here (`` `delete-${chatId}` ``), so this is
		// normally the single native <button> element - not the array Vue returns when several
		// v-for iterations share the same static ref name. Handled defensively either way.
		focusRef (name) {
			const target = this.$refs[name]
			const el = Array.isArray(target) ? target[0] : target
			if (el && typeof el.focus === 'function') el.focus()
		}
	}
}
</script>

<style lang="scss" scoped>
.help-chat-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid var(--border, #E0E2E5);
  // A subtle wash, as React's `bg-muted/40` gives this section, so the chat list reads as a
  // separate surface from the transcript beneath it rather than floating on the same one.
  // Solid fallback first, for a browser without color-mix.
  background-color: var(--muted, #F5F5F5);
  background-color: color-mix(in srgb, var(--muted, #F5F5F5) 40%, transparent);

  /*
    Matches the React ChatList's New chat button, which resolves to an outline Button with
    `h-auto px-3 py-1.5 text-[13px]`: a 6px radius, the page background rather than the card
    surface, and an accent fill on hover rather than a muted one (AB#27211).
  */
  &__new-chat {
    align-self: flex-start;
    min-height: 0;
    padding: 6px 12px;
    background-color: var(--background, #F5F5F5);
    border-radius: calc(var(--radius-md, 0.5rem) - 2px);

    &:hover:not(:disabled) {
      color: var(--accent-foreground, #FFFFFF);
      background-color: var(--accent, #5C7B9D);
    }
  }

  &__hint {
    margin: 0;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);

    &--loading {
      display: flex;
      gap: 8px;
      align-items: center;
    }
  }

  &__spinner {
    flex: none;
    animation: help-chat-list-spin 1s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 200px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__select {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    padding: 6px 8px;
    text-align: left;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: var(--muted, #F5F5F5);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    // The active row took a full accent fill, which shouted over everything else in the panel and
    // left its own timestamp barely legible. Muted fill plus a primary badge instead (AB#27211).
    &--active {
      background-color: var(--muted, #F5F5F5);
    }
  }

  &__select-heading {
    display: flex;
    gap: 8px;
    align-items: center;
    width: 100%;
    min-width: 0;
  }

  &__select-badge {
    flex: none;
    padding: 2px 8px;
    font-size: 0.625rem;
    font-weight: 600;
    color: var(--primary-foreground, #FFFFFF);
    background-color: var(--primary, #406A94);
    border-radius: var(--radius-full, 9999px);
  }

  &__select-title {
    // min-width lets it shrink inside the heading row, so the badge is never pushed out of view.
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--foreground, #4A5568);
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.15s ease;
  }

  &__select-time {
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
    transition: color 0.15s ease;
  }

  &__icon-button {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 24px;
    color: var(--muted-foreground, #666E7A);
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: var(--accent, #5C7B9D);
      color: var(--accent-foreground, #FFFFFF);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /*
      Hover fills with the brand colour rather than tinting the glyph over a neutral accent, which
      read as a hover on any other control. Mirrors the React ChatList (AB#27211).
    */
    &--emphasis:hover:not(:disabled) {
      color: var(--primary-foreground, #FFFFFF);
      background-color: var(--primary, #406A94);
    }

    &--emphasis:focus-visible {
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 0;
    }
  }
}

@keyframes help-chat-list-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
